import React, { useEffect, useMemo, useState } from 'react'
import { Divider, Grid, Pagination, Theme, useMediaQuery, Button } from '@material-ui/core'
import Tabs from 'components/Tabs'
import useIntl from 'hooks/useIntl'
import { TeamContextProps, useTeamContext } from 'screens/Teams/Teams.config'
import { pxToRem } from 'packages/nuggetai.ui-kit/themes/utils'
import TalentSearchList from './TalentSearchList'
import { setSelectedTalent, useTalentContext } from '../context'
import { useGlobalWebSocket } from 'context/WebSocketContext'
import * as XLSX from 'xlsx'
import { useApolloClient } from '@apollo/client'
import { GET_APPLICANTS } from 'constants/queries/applicant'

export interface TalentSearchProps {}

const teamSelector = (store: TeamContextProps) => ({
	applicants: store.applicants,
	loading: store.applicantsLoading,
	page: store.page,
	getApplicants: store.getApplicants,
	teamId: store.teamId,
	handlePageChange: store.handlePageChange,
	refetchApplicants: store.refetchApplicants,
	sort: store.sort
})

export const TalentSearch: React.FC<TalentSearchProps> = () => {
	const intl = useIntl()
	const [isFirst, setIsFirst] = useState(true)
	const [pickFirst, setPickFirst] = useState(true)
	const [, dispatch] = useTalentContext()
	const {
		applicants,
		loading,
		page,
		getApplicants,
		teamId,
		handlePageChange,
		refetchApplicants,
		sort
	} = useTeamContext(teamSelector)
	const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))

	const [allApplicants, setAllApplicants] = useState<any[]>([])

	useEffect(() => {
		setAllApplicants([])
	}, [teamId, sort])

	useEffect(() => {
		getApplicants({
			variables: {
				assessId: teamId,
				sort,
				filter: null,
				page: 1
			}
		})
		// eslint-disable-next-line
	}, [teamId])
	useEffect(() => {
		if (!isFirst) {
			getApplicants({
				variables: {
					assessId: teamId,
					sort,
					filter: null,
					page
				}
			})
		} else {
			setIsFirst(false)
		}
	}, [page, sort])

	const { registerRefetch } = useGlobalWebSocket()

	useEffect(() => {
		const unregister = registerRefetch(refetchApplicants)
		return () => unregister()
	}, [registerRefetch, refetchApplicants])

	const handleChangeTab = async (newTab: number) => {
		const newApplicants = await refetchApplicants(
			newTab === 1 ? 'Internal' : newTab === 2 ? 'External' : null
		)
		const talent = newApplicants?.items?.[0]
		if (talent && !smDown) {
			dispatch(
				setSelectedTalent({
					...(talent?.user as { firstName: string; lastName: string }),
					id: talent?.id,
					pipelineType: talent?.pipelineType,
					pipelineId: talent?.pipelinesId[0] as string,
					pipelinesId: talent?.pipelinesId,
					masked: !!talent?.masking,
					score: talent?.score || null
				})
			)
		}
		handlePageChange(undefined, 1)
	}

	const TalentList = useMemo(
		() => (
			<TalentSearchList
				pickFirst={pickFirst}
				smDown={smDown}
				onPickFirst={() => {
					setPickFirst(false)
				}}
				loading={loading}
				talents={applicants?.items || []}
			/>
		),
		[applicants, loading, pickFirst, smDown]
	)

	const [hasFetchedAllPages, setHasFetchedAllPages] = useState(false)
	useEffect(() => {
		if (hasFetchedAllPages) return
		if (page === 1 && applicants?.items) {
			setAllApplicants(applicants.items)
		}
	}, [applicants?.items, page, hasFetchedAllPages])
	type ApplicantType = {
		id: string
	}

	const [loadingAllPages, setLoadingAllPages] = useState(false)
	const client = useApolloClient()

	const fetchAllApplicants = async () => {
		if (!teamId || !sort || !applicants?.pageInfo?.pageCount || loadingAllPages) return

		setLoadingAllPages(true)

		try {
			const pages = applicants.pageInfo.pageCount
			const currentPage = applicants.pageInfo.currentPage
			const combined: ApplicantType[] = [...(applicants.items || [])]

			const fetchPromises: Promise<ApplicantType[]>[] = []

			for (let i = 1; i <= pages; i++) {
				if (i === currentPage) continue
				fetchPromises.push(
					client
						.query({
							query: GET_APPLICANTS,
							variables: {
								assessId: teamId,
								sort,
								filter: null,
								page: i
							},
							fetchPolicy: 'network-only'
						})
						.then(res => res?.data?.getApplicants?.items || [])
						.catch(err => {
							console.error(`Error fetching page ${i}:`, err)
							return []
						})
				)
			}

			const results = await Promise.all(fetchPromises)
			results.forEach(pageItems => combined.push(...pageItems))

			// Remove duplicates by applicant id
			const uniqueApplicants = Array.from(
				new Map(combined.map(app => [app.id, app])).values()
			)

			setAllApplicants(uniqueApplicants)
			setHasFetchedAllPages(true)
		} catch (err) {
			console.error('Error in fetchAllApplicants:', err)
		} finally {
			setLoadingAllPages(false)
		}
	}
	useEffect(() => {
		if (
			applicants?.pageInfo?.pageCount &&
			!isFirst &&
			!hasFetchedAllPages &&
			!loadingAllPages
		) {
			fetchAllApplicants()
		}
	}, [
		teamId,
		sort,
		applicants?.pageInfo?.pageCount,
		isFirst,
		hasFetchedAllPages,
		loadingAllPages
	])

	return (
		<Grid container item>
			<Grid container item>
				<Grid item xs={12} id="team-tours-talent-search" style={{ height: '100%' }}>
					<Tabs
						onChange={handleChangeTab}
						tabs={[
							{
								label: intl.formatMessage(
									{ id: 'teams.talent.all' },
									{ total: applicants?.totalTalents.count || 0 }
								),
								component: TalentList
							},
							{
								label: intl.formatMessage(
									{ id: 'teams.talent.employee' },
									{ total: applicants?.totalTalents.employees || 0 }
								),
								component: TalentList
							},
							{
								label: intl.formatMessage(
									{ id: 'teams.talent.candidate' },
									{ total: applicants?.totalTalents.candidates || 0 }
								),
								component: TalentList
							}
						]}
						variant="fullWidth"
						tabStyle={{ fontSize: pxToRem(13), height: 56 }}
						centered
					/>
				</Grid>
			</Grid>
			{(applicants?.items || []).length > 0 && (
				<Grid item xs={12}>
					<Divider />
				</Grid>
			)}
			{!applicants?.pageInfo.pageCount ? null : (
				<Grid sx={{ padding: '10px' }} container justifyContent="center">
					<Pagination
						page={page}
						onChange={handlePageChange}
						count={applicants.pageInfo.pageCount}
					/>
				</Grid>
			)}
		</Grid>
	)
}

export default TalentSearch
